/* eslint-disable @atlaskit/design-system/use-spotlight-package */
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageEvent } from '@post-office/message-lifecycle';

import IconButton from '@atlaskit/button/icon/button';

import { cssMap } from '@atlaskit/css';

import CloseIcon from '@atlaskit/icon/core/cross';

import Image from '@atlaskit/image';
import SpotlightCard from '@atlaskit/onboarding/spotlight-card';
import { type Actions } from '@atlaskit/onboarding/types';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import Skeleton from '@atlaskit/skeleton';

import { token } from '@atlaskit/tokens';

import { getGlobalTheme } from '@atlaskit/tokens/get-global-theme';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { camelCase } from 'lodash';

import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { buttonMessages } from './button-messages';

import { SPOTLIGHT_WIDTH } from './constants';

import { type JSX } from 'react/jsx-runtime';

type TwcSpotlightProps = {
	spotlightEnd: () => void;
	primaryActionFunction: () => void;
	primaryInteraction: 'next' | 'acknowledged' | 'ctaActioned';
	secondaryActionFunction?: () => void;
	primaryActionText: string;
	secondaryActionText?: string;
	lightSrcImage: string;
	darkSrcImage: string;
	heading: string;
	body: string;
	testId: string;
	currentSpotlightNumber: number;
	numTotalSpotlights: number;
	spotlightType: 'for_you' | 'rovo' | 'switcher';
};

const styles = cssMap({
	root: {
		marginBlock: token('space.negative.200'),
		marginInlineStart: token('space.negative.250'),
	},
	boxAroundIconButton: {
		position: 'absolute',
		insetInlineEnd: token('space.0'),
		marginBlockStart: token('space.150'),
		marginInlineEnd: token('space.100'),
	},
	imageHiddenStyles: {
		display: 'none',
	},
	skeletonBackground: {
		backgroundColor: token('color.background.accent.gray.subtle'),
		height: '132px',
		width: '100%',
	},
});

export const TwcSpotlightCard = (props: TwcSpotlightProps): JSX.Element => {
	const {
		spotlightEnd,
		primaryActionFunction,
		primaryInteraction,
		primaryActionText,
		secondaryActionFunction,
		secondaryActionText,
		lightSrcImage,
		darkSrcImage,
		heading,
		body,
		testId,
		currentSpotlightNumber,
		numTotalSpotlights,
		spotlightType,
	} = props;
	const intl = useIntl();

	const { messageInstanceViewed, messageInstanceClicked } = useMessageInstanceEvent();
	const { onClosed, onAcknowledged, onCtaActioned, onStepChanged } =
		usePostOfficeUserInteractions();
	const { messageClicked } = useMessageEvent();
	const [imageLoaded, setImageLoaded] = useState(false);

	const actions: Actions = [
		{
			onClick: () => {
				if (fg('post-office_enable_semantic-event-fixes')) {
					// Preserve existing click-filter suppression separately from step analytics.
					void messageClicked().catch(() => {});
					const additionalMetadata = { spotlightType, numTotalSpotlights };
					if (primaryInteraction === 'next') {
						onStepChanged({
							interactionIdentifier: 'next',
							direction: 'next',
							fromStep: currentSpotlightNumber,
							toStep: currentSpotlightNumber + 1,
							additionalMetadata,
						});
					} else if (primaryInteraction === 'ctaActioned') {
						onCtaActioned({ interactionIdentifier: 'open-rovo-chat', additionalMetadata });
					} else {
						onAcknowledged({ interactionIdentifier: 'finish-tour', additionalMetadata });
					}
				} else {
					void messageInstanceClicked({
						additionalMetadataFromFrontend: { spotlightType, numTotalSpotlights },
						action: {
							actionType: 'open',
						},
						messageEventType: 'clicked',
						clickedElement: {
							clickedElement: 'button',
							buttonType: 'primary',
							clickIdentifier: camelCase(primaryActionText),
						},
					});
				}
				primaryActionFunction();
			},
			text: primaryActionText,
			autoFocus: true,
		},
	];

	if (secondaryActionFunction && secondaryActionText) {
		actions.push({
			onClick: () => {
				if (fg('post-office_enable_semantic-event-fixes')) {
					onStepChanged({
						interactionIdentifier: 'back',
						direction: 'previous',
						fromStep: currentSpotlightNumber,
						toStep: currentSpotlightNumber - 1,
						additionalMetadata: { spotlightType, numTotalSpotlights },
					});
				} else {
					void messageInstanceClicked({
						additionalMetadataFromFrontend: { spotlightType, numTotalSpotlights },
						action: {
							actionType: 'open',
						},
						messageEventType: 'clicked',
						clickedElement: {
							clickedElement: 'button',
							buttonType: 'secondary',
							clickIdentifier: camelCase(secondaryActionText),
						},
					});
				}
				secondaryActionFunction();
			},
			text: secondaryActionText,
			appearance: 'subtle',
		});
	}

	const progress = intl.formatMessage(
		{
			defaultMessage: '{currentSpotlightNumber}/{numTotalSpotlights}',
			description:
				"User's progress through spotlight tour where numTotalSpotlights indicates total number of spotlights in the tour and currentSpotlightNumber, the current position in the tour.",
			id: 'townsquare.townsquare-twc-modal.progress',
		},
		{
			currentSpotlightNumber,
			numTotalSpotlights,
		},
	);

	useEffect(() => {
		void messageInstanceViewed({ spotlightType, numTotalSpotlights });
	}, [messageInstanceViewed, spotlightType, numTotalSpotlights]);

	const isDarkMode = getGlobalTheme().colorMode === 'dark';

	return (
		<Box xcss={styles.root}>
			<Box xcss={styles.boxAroundIconButton}>
				<IconButton
					icon={CloseIcon}
					appearance="subtle"
					onClick={() => {
						if (fg('post-office_enable_semantic-event-fixes')) {
							onClosed({ interactionIdentifier: 'close-tour' });
						}
						spotlightEnd();
					}}
					label={intl.formatMessage(buttonMessages.closeButtonLabel)}
					isTooltipDisabled={false}
					testId="close-spotlight"
				/>
			</Box>
			<SpotlightCard
				width={SPOTLIGHT_WIDTH}
				isFlat
				actions={actions}
				actionsBeforeElement={progress}
				heading={heading}
				image={
					<Stack>
						{!imageLoaded && (
							<Stack xcss={styles.skeletonBackground}>
								<Skeleton
									isShimmering
									height="100%"
									width="100%"
									borderRadius={token('radius.large')}
								/>
							</Stack>
						)}
						{/* Hide image until its loaded to prevent both the skeleton and image appearing at the same time. */}
						<Stack xcss={!imageLoaded ? styles.imageHiddenStyles : undefined}>
							<Image
								src={isDarkMode ? darkSrcImage : lightSrcImage}
								width={SPOTLIGHT_WIDTH}
								alt=""
								onLoad={() => setImageLoaded(true)}
							/>
						</Stack>
					</Stack>
				}
				testId={testId}
			>
				{body}
			</SpotlightCard>
		</Box>
	);
};
